import React from 'react';
import { motion } from 'framer-motion';
import { FiBookOpen, FiTarget, FiTrendingUp, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const steps = [
  {
    id: 1,
    title: "Educate & Learn",
    description: "Dive into our comprehensive curriculum. Learn the exact frameworks to identify, analyze, and acquire undervalued land.",
    icon: FiBookOpen,
  },
  {
    id: 2,
    title: "Find & Analyze Deals",
    description: "Use our proprietary tools and strategies to source off-market properties and evaluate their true profit potential.",
    icon: FiTarget,
  },
  {
    id: 3,
    title: "Flip & Profit",
    description: "Execute the transaction with confidence. Learn how to market and sell the land for a significant return on investment.",
    icon: FiTrendingUp,
  }
];

const HowItWorks = () => {
  return (
    <section className="py-32 bg-slate-50 relative overflow-hidden" id="how-it-works">
      {/* Decorative Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 font-semibold text-sm mb-6"
          >
            The Curriculum
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900"
          >
            Your Path to Land Flipping Success
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="mt-6 max-w-2xl text-xl text-slate-600 mx-auto font-light"
          >
            We provide a step-by-step roadmap to help our students make money in the Real Estate Education industry.
          </motion.p>
        </div>

        <div className="relative">
          {/* Connecting dashed line for desktop */}
          <div className="hidden md:block absolute top-16 left-[15%] right-[15%] border-t-2 border-dashed border-slate-300" aria-hidden="true" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 lg:gap-8">
            {steps.map((step, index) => (
              <motion.div 
                key={step.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative flex flex-col items-center text-center group"
              >
                {/* Number Badge */}
                <div className="absolute -top-4 z-20 bg-slate-900 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg border-2 border-slate-50 transition-transform group-hover:scale-110">
                  {step.id}
                </div>
                
                {/* Icon Container */}
                <div className="w-32 h-32 rounded-3xl bg-white shadow-xl shadow-slate-200/50 flex items-center justify-center z-10 relative mb-8 border border-slate-100 transition-all duration-300 group-hover:-translate-y-2 group-hover:shadow-2xl group-hover:shadow-emerald-500/10">
                  <div className="w-24 h-24 rounded-2xl bg-emerald-50 flex items-center justify-center transition-colors group-hover:bg-emerald-100">
                    <SafeIcon icon={step.icon} className="text-4xl text-emerald-600" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-slate-900 mb-4">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed px-4 font-light text-lg">
                  {step.description}
                </p>

                {/* Mobile Connector Arrow */}
                {index !== steps.length - 1 && (
                  <div className="md:hidden mt-8 text-slate-300">
                    <SafeIcon icon={FiArrowRight} className="text-3xl rotate-90" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;